import React, { MouseEventHandler, useCallback, useContext, useEffect, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import TextButton from 'LuxKit/Button/TextButton'
import { pluralizeToString } from 'lib/string/pluralize'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Label from 'LuxKit/Label'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import BedbankMarginText from './BedbankMargin'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { useInView } from 'react-intersection-observer'
import * as Analytics from 'analytics/analytics'
import { bedbankRateImpressionEventWithContext } from 'analytics/snowplow/events'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSource } from 'selectors/offerSelectors'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import { SOURCE_TYPES } from 'constants/source'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PayLaterPill from 'components/Common/PayLaterPill'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import { getRelatedPayAtPropertyRate, hasMoreThanOnePropertyCollectRelation, shouldShowPayAtPropertyPill, shouldShowPayLaterPill } from '../utils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import BedbankPayAtPropertyModal from './Modals/BedbankPayAtPropertyModal'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'

const FlexAutoGroup = styled(Group)`
  & > * {
    flex: auto;
  }
`

interface Props {
  promotion?: App.BedbankPromotion
  rate: App.BedbankRate
  payAtPropertyRates: Array<App.BedbankRate>
  bundleRate?: App.BedbankRate
  rooms: Array<OfferPageRoom>
  offer: App.BedbankOffer | App.BedbankOfferSummary
  flightOrigin?: App.Airport
  flightPrice?: number
  checkIn?: string
  checkOut?: string
  departureTooEarly?: boolean
  flightsEnabled?: boolean
  ratePosition: number
  rateListId?: string
  roomPosition?: number
  roomListId?: string
  luxPlusLPPEnabled: boolean
  userMultiItemCartEnabled: boolean
  isFlightsSelected: boolean
  isLoggedIn: boolean
  showPoints: boolean
  onBookNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    keepBrowsing?: boolean,
    isInsuranceSelected?: boolean
  ) => void
  onAddToUserCartNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    isInsuranceSelected?: boolean
  ) => void
  isInsuranceSelected?: boolean
}

function BedbankPackageTilePricing(props: Props) {
  const {
    rate,
    payAtPropertyRates,
    bundleRate,
    promotion,
    rooms,
    offer,
    checkIn,
    checkOut,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    ratePosition,
    rateListId,
    roomPosition,
    roomListId,
    onBookNow,
    onAddToUserCartNow,
    luxPlusLPPEnabled,
    userMultiItemCartEnabled,
    isFlightsSelected,
    isLoggedIn,
    showPoints,
    isInsuranceSelected
  } = props

  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: offer.flights?.airportCode,
  })
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const {
    propertyFees = 0,
    taxesAndFees,
  } = rate.totals
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees

  const hotelPrice = rate.totals.inclusive
  const hotelMemberPrice = rate.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  const showFlightsDealTag = flightOrigin?.code && offer.flights?.airportCode && flightsEnabled && deals.length > 0

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > 330
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const canAddToUserCart = hasDates && userMultiItemCartEnabled

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue: rate.value,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue: bundleRate?.value,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'
  const isPromoRate = rate.id === promotion?.id
  const isPayAtPropertyFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croPayAtProperty)

  const margin = rate.margin
  const marginAud = rate.marginAud
  const supplier = rate.supplier
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(margin && marginAud && supplier && isSpoofed && showMarginInSpoofMode)
  const discount = useBedbankDiscount(rate.discount, rate.memberDiscount)
  const source = useAppSelector(state => getSource(state))

  const bundleMargin = bundleRate?.margin
  const bundleMarginAud = bundleRate?.marginAud
  const bundleSupplier = bundleRate?.supplier
  const displayBundleMargin = Boolean(bundleMargin && bundleMarginAud && bundleSupplier && isSpoofed && showMarginInSpoofMode)
  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const currency = useContext(CurrencyContext)

  const [ref, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (inView && checkIn && checkOut && rateListId && roomPosition && roomListId) {
      Analytics.trackEvent(bedbankRateImpressionEventWithContext({
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        offer,
        rate,
        price: totalPrices.totals.price,
        value: totalPrices.totals.value,
        checkIn,
        checkOut,
        occupancy: rooms,
        currency,
        isFlightBundle: false,
      }))
    }
  }, [inView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, rate, totalPrices.totals.price, totalPrices.totals.value, rooms, currency])

  useEffect(() => {
    if (inView && checkIn && checkOut && bundleRate && canShowFlightBundlePrice && rateListId && roomPosition && roomListId) {
      Analytics.trackEvent(bedbankRateImpressionEventWithContext({
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        offer,
        rate: bundleRate,
        price: totalPrices.totals.flightBundlePrice,
        value: totalPrices.totals.flightBundleValue,
        checkIn,
        checkOut,
        occupancy: rooms,
        currency,
        isFlightBundle: true,
      }))
    }
  }, [inView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, bundleRate, totalPrices.totals.flightBundlePrice, totalPrices.totals.flightBundleValue, rooms, currency, canShowFlightBundlePrice])

  const relatedPayAtPropertyRate = useMemo(() => getRelatedPayAtPropertyRate(payAtPropertyRates, rate), [payAtPropertyRates, rate])

  const getChosenRate = useCallback(async(hasFlight: boolean): Promise<App.BedbankRate | undefined> => {
    if (!hasFlight && isPayAtPropertyFeatureEnabled) {
      const propertyCountryCode = offer.property.address.countryCode

      if (relatedPayAtPropertyRate) {
        const chosenRate = await showModal<App.BedbankRate | undefined>(<BedbankPayAtPropertyModal payAtPropertyRate={relatedPayAtPropertyRate} lePaymentNowRate={rate} propertyCountryCode={propertyCountryCode} />)
        return chosenRate
      }
    }

    return rate
  }, [relatedPayAtPropertyRate, rate, isPayAtPropertyFeatureEnabled, showModal, offer.property.address.countryCode])

  const onBookNowHandler = useMemo<MouseEventHandler<HTMLButtonElement> | undefined>(() => {
    if (!onBookNow) { return undefined }
    return async(event) => {
      const keepBrowsing: boolean = !!event.currentTarget.dataset.keepBrowsing
      const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight

      const chosenRate = await getChosenRate(hasFlight)
      if (!chosenRate) {
        return
      }

      onBookNow(chosenRate, ratePosition, hasFlight, keepBrowsing, isInsuranceSelected)
    }
  }, [onBookNow, getChosenRate, ratePosition, isInsuranceSelected])

  const onAddToUserCartHandler = useMemo<MouseEventHandler<HTMLButtonElement> | undefined>(() => {
    if (!onAddToUserCartNow) { return undefined }
    return async(event) => {
      const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight

      const chosenRate = await getChosenRate(hasFlight)
      if (!chosenRate) {
        return
      }

      onAddToUserCartNow(chosenRate, ratePosition, hasFlight, isInsuranceSelected)
    }
  }, [onAddToUserCartNow, getChosenRate, ratePosition, isInsuranceSelected])

  const isMetaSearch = useMemo(() => {
    return source?.includes(SOURCE_TYPES.METASEARCH)
  }, [source])

  const onLoginForMemberSavings = useCallback(async() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const shouldDisplayLoginForMemberSavings = !isLoggedIn && isMetaSearch

  const showHotelAndFlightsToggle = flightsEnabled
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!showPoints) { return [] }
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(rate, {
        price: totalPrices.totals.price,
        memberPrice: totalPrices.totals.memberPrice,
        propertyFees,
        propertyId: offer.property.id,
        offerId: offer.id,
        propertyCountryCode: offer.property.address.countryCode,
        currentRegionCode,
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !canShowFlightBundlePrice || hideHotelAndFlightsPriceBlock,
      }),
    ]
  }, [
    rate,
    totalPrices.totals.price,
    totalPrices.totals.memberPrice,
    propertyFees,
    offer,
    flightPrice,
    canShowFlightBundlePrice,
    hideHotelAndFlightsPriceBlock,
    showPoints,
    currentRegionCode,
  ])

  const showFlightsPricing = flightsEnabled && flightOrigin && !hideHotelAndFlightsPriceBlock
  const canPayAtProperty = !showFlightsPricing && shouldShowPayAtPropertyPill(rate)
  const canPayLater = shouldShowPayLaterPill(rate) && !hideHotelPriceBlock

  let hotelOnlyBookNowText = 'Book now'
  if (rooms.length > 1) {
    hotelOnlyBookNowText = `Book ${pluralizeToString(offer.saleUnit, rooms.length)}`
  }

  return <>
    <Group direction="horizontal" gap={4}>
      {canPayLater && <PayLaterPill />}
      {canPayAtProperty && <PayAtPropertyPill hasMultipleOptions={hasMoreThanOnePropertyCollectRelation(rate)} />}
    </Group>

    <AccommodationPackageTilePricingBlocks>
      {!hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={(flightsEnabled && !showHotelAndFlightsToggle) ? 'Hotel' : undefined}
        callToAction={<FlexAutoGroup direction="horizontal" wrap="wrap" tabletDirection="vertical" gap={8} noDisplayWhenEmpty>
          {shouldDisplayLoginForMemberSavings && <TextButton size="large" kind="primary" nonInteractable onClick={onLoginForMemberSavings}>
            Sign up & save more
          </TextButton>}
          {canAddToUserCart && onAddToUserCartHandler && (
            <TextButton
              size="medium"
              onClick={onAddToUserCartHandler}
              startIcon={<LineShoppingCartIcon />}
              kind="secondary"
            >
              Add to cart
            </TextButton>
          )}
          {!shouldDisplayLoginForMemberSavings && onBookNowHandler && <TextButton
            size="medium"
            kind="primary"
            data-testid="book-button"
            onClick={onBookNowHandler}
          >
            {hasDates ? hotelOnlyBookNowText : 'Check availability'}
          </TextButton>}
        </FlexAutoGroup>}
      >
        <VerticalSpacer gap={12}>
          <div>
            <PriceRowPriceCaption>
              {pluralizeToString('night', rate.nights)}{rooms.length > 1 ? `, ${pluralizeToString(offer.saleUnit, rooms.length)}` : ''}{hasDates ? ' from' : ''}
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                data-testid="bedbank-package-price"
                size="M"
                price={totalPrices.totals.price}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.price}
                memberPrice={totalPrices.totals.memberPrice}
                saleUnit={saleUnit}
              />
            </LuxPlusPriceGuard>
            <div ref={ref}></div>
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            {!isPromoRate && <PriceRowTaxesAndFees />}

            <PriceRowAgentHubCommission
              size="M"
              productType={offer.productType}
              offerLocations={[]}
              offerId={offer.id}
              packageId={rate.id}
              offerTotal={totalPrices.original.price}
              kind="plain"
            />

            {!!discount && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.value}
              discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
              discountType="estimate"
            />}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={propertyFees}
              taxesAndFees={taxesAndFees}
              hotelPrice={hotelPrice}
              hotelMemberPrice={hotelMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
              duration={rate.nights}
              hasMobilePromotion={rate.mobilePromotion}
              hasRelatedPayAtPropertyRate={!!relatedPayAtPropertyRate}
              rateId={rate.id}
              cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8} noDisplayWhenEmpty>
                {onBookNowHandler && <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                  {hasDates ? hotelOnlyBookNowText : 'Check availability'}
                </TextButton>}
                {canAddToUserCart && onAddToUserCartHandler && <TextButton
                  size="large"
                  onClick={onAddToUserCartHandler}
                  startIcon={<LineShoppingCartIcon />}
                  kind="secondary"
                >
                  Add to cart
                </TextButton>}
              </Group>}
            />}
            {displayMargin && <BedbankMarginText
              margin={margin!}
              marginAud={marginAud!}
              supplier={supplier!}
              type="bedbank_exact"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={!checkIn ? 'estimate' : 'exact'}
          />
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}
      {showFlightsPricing && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title={!showHotelAndFlightsToggle ? 'Hotel + flights' : undefined}
        callToAction={<FlexAutoGroup direction="horizontal" wrap="wrap" tabletDirection="vertical" gap={8} noDisplayWhenEmpty>
          {flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && <>
            {canAddToUserCart && onAddToUserCartHandler && <TextButton
              size="medium"
              data-has-flight
              data-testid="book-button"
              onClick={onAddToUserCartHandler}
              kind="primary"
            >
              Select package
            </TextButton>}
            {!canAddToUserCart && onBookNowHandler && <TextButton
              size="medium"
              kind="primary"
              data-testid="book-button"
              data-has-flight
              onClick={onBookNowHandler}
            >
              {hasDates ? 'Book now' : 'Check availability'}
            </TextButton>}
          </>}
          {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <TextButton
            size="medium"
            data-filter="airport"
            kind="tertiary"
            onClick={onToggleFilter}
          >
            Change airport
          </TextButton>}
          {flightOrigin.code !== offer.flights?.airportCode && (departureTooEarly || departureTooFarInFuture) && <TextButton
            size="medium"
            data-filter="checkin"
            kind="tertiary"
            onClick={onToggleFilter}
          >
            Change departure date
          </TextButton>}
        </FlexAutoGroup>}
      >
        <VerticalSpacer gap={12}>
          {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <BodyText
            variant="small"
            colour="neutral-two"
          >
            No flights available from {flightOrigin.name}. Please select another departing airport.
          </BodyText>}
          {!hasFlights && flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
            <BodyText variant="small" colour="neutral-two">
              Add flights to the hotel package. Select from all major airlines at the next step.
            </BodyText>
          )}
          {!hasFlights && departureTooFarInFuture && (
            <BodyText variant="small" colour="neutral-two">
              Flights can only be booked up to 11 months in advance.
            </BodyText>
          )}
          {flightOrigin.code !== offer.flights?.airportCode && departureTooEarly && <BodyText
            variant="small"
            colour="neutral-two"
          >
            Flights cannot be booked online when check-in is within 24 hours
          </BodyText>}
        </VerticalSpacer>
        {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
          <div>
            <PriceRowPriceCaption>
              {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', rate.nights)}{rooms.length > 1 ? `, ${pluralizeToString(offer.saleUnit, rooms.length)}` : ''} + flights from
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.flightBundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                memberPrice={totalPrices.totals.flightBundleMemberPrice}
                saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total*'}
              />
            </LuxPlusPriceGuard>
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            <PriceRowTaxesAndFees />
            {discount > 0 && !isPromoRate && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.flightBundleValue}
              discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
              discountType="estimate"
            />}
            {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={bundlePropertyFees}
              taxesAndFees={bundleTaxesAndFees}
              hotelPrice={bundlePrice}
              hotelMemberPrice={bundleMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && bundleMemberPrice > 0}
              flightPrice={flightPrice}
              duration={rate.nights}
              hasRelatedPayAtPropertyRate={!!relatedPayAtPropertyRate}
              cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8} noDisplayWhenEmpty>
                {canAddToUserCart && onAddToUserCartHandler && <TextButton
                  size="large"
                  onClick={onAddToUserCartHandler}
                  data-has-flight
                  kind="primary"
                >
                  Select package
                </TextButton>}
                {(!userMultiItemCartEnabled || !hasDates) && onBookNowHandler && <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                  {hasDates ? 'Book now' : 'Check availability'}
                </TextButton>}
              </Group>}
            />}
            {displayBundleMargin && <BedbankMarginText
              margin={bundleMargin!}
              marginAud={bundleMarginAud!}
              supplier={bundleSupplier!}
              type="bedbank_exact_accom"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
          />
        </VerticalSpacer>}
        <AtolProtectedTooltip />
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
  </>
}

type MappedProps = Pick<Props,
  | 'luxPlusLPPEnabled'
  | 'userMultiItemCartEnabled'
  | 'isLoggedIn'
  | 'isFlightsSelected'
  | 'checkIn'
  | 'checkOut'
  | 'flightsEnabled'
  | 'flightOrigin'
  | 'offer'
  | 'rooms'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isFlightsSelected: state.flights,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  flightOrigin: state.airport,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
}), BedbankPackageTilePricing)
